/**
 * Create the store with asynchronously loaded reducers
 */

import Immutable, { fromJS } from 'immutable';
import {
  applyMiddleware,
  compose,
  createStore,
  type Middleware,
  type StoreEnhancer,
} from 'redux';
import thunk from 'redux-thunk';

import apiMiddleware from './middleware/apiMiddleware';
import createReducer, {
  type LegacyReducerRegistry,
  type LegacyRootState,
} from './createReducers';

const toLegacyRootState = (
  initialState: Record<string, unknown>,
): LegacyRootState => {
  const state: unknown = fromJS(initialState);

  if (!Immutable.Map.isMap(state)) {
    throw new TypeError(
      'Expected the initial Redux state to be an Immutable Map',
    );
  }

  return state as LegacyRootState;
};

function configureStore(initialState: Record<string, unknown> = {}) {
  let composeEnhancers = compose;

  // If Redux Dev Tools and Saga Dev Tools Extensions are installed, enable them
  /* istanbul ignore next */
  if (process.env.NODE_ENV !== 'production' && typeof window === 'object') {
    const reduxDevToolsCompose = (
      window as Window & {
        __REDUX_DEVTOOLS_EXTENSION_COMPOSE__?: (options: {
          trace: boolean;
          traceLimit: number;
        }) => typeof compose;
      }
    ).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;

    if (reduxDevToolsCompose)
      composeEnhancers = reduxDevToolsCompose({
        trace: true,
        traceLimit: 25,
      });
  }

  const legacyApiMiddleware: Middleware = apiMiddleware;
  const middlewareEnhancer: StoreEnhancer = applyMiddleware(
    thunk,
    legacyApiMiddleware,
  );

  const reduxStore = createStore(
    createReducer(),
    toLegacyRootState(initialState),
    composeEnhancers(middlewareEnhancer),
  );

  // Extensions
  const injectedReducers: LegacyReducerRegistry = {};

  return Object.assign(reduxStore, { injectedReducers });
}

export const store = configureStore({});
